import moment, { Moment } from "moment";
import { SUPPORTED_LANGUAGES } from "./constants";
import { parsePhoneNumber } from "awesome-phonenumber";

interface CustomWindow extends Window {
  FlutterWebviewPlugin?: any; // Adjust type as necessary
  TripsMerchant?: any;
}

declare var window: CustomWindow;

export const pluralizeText = (total: number, singularText: string, pluralText: string): string =>
  total === 1 ? `1 ${singularText}` : `${total} ${pluralText}`;

export const formatNumberAsCurrency = (value: number, currency?: string) => {
  let configCurrency = "";
  try {
    const auth = localStorage.getItem("trips_auth");
    if (auth) {
      const obj: UserAuth = JSON.parse(auth);
      configCurrency = obj.ExtraData?.CurrencyCode || "";
    }
  } catch (e) { }

  const displayCurrency = currency || configCurrency || "NGN";
  return `${displayCurrency}\u00a0${value.toLocaleString("en-US")}`;
};

export const formatNumber = (value: number) => value.toLocaleString("en-US");

export const getThemeName = () => {
  const auth = localStorage.getItem("trips_auth");
  if (auth) {
    const obj: UserAuth = JSON.parse(auth);
    return obj.ExtraData?.PrimaryColor;
  }

  return "";
};

export const getUser = () => {
  const user = localStorage.getItem("trips_user");

  if (user) {
    const obj: User = JSON.parse(user);

    return obj;
  }

  return null;
};

export const getExtraData = () => {
  const config = localStorage.getItem("trips_auth");

  if (config) {
    const obj = JSON.parse(config);

    return obj && obj.ExtraData;
  }

  return { MultiCityMaxRoute: null };
};

export const getFontSize = (size?: string) => {
  let fontSize = 1;
  switch (size) {
    case "SMALL":
      fontSize = 0.8;
      break;
    case "NORMAL":
      fontSize = 1;
      break;
    case "MEDIUM":
      fontSize = 1.3;
      break;
    case "LARGE":
      fontSize = 1.5;
      break;
    case "EXTRA_LARGE":
      fontSize = 2;
      break;
    default:
      fontSize = 1;
  }

  return fontSize;
};

export const postMessage = (result: any) => {
  const stringifyObj = JSON.stringify(result);

  // Flag to track if any specific bridge was used
  let bridgeUsed = false;
  if (window.TripsMerchant) {
    window.TripsMerchant.postMessage(stringifyObj);
    bridgeUsed = true;
  }
  if (window.FlutterWebviewPlugin || window.flutter_inappwebview) {
    // post message to Flutter
    window.FlutterWebviewPlugin.postMessage(stringifyObj);
    window.flutter_inappwebview.postMessage(stringifyObj);
    bridgeUsed = true;
  }
  if (window.ReactNativeWebView) {
    // post message to react native
    window.ReactNativeWebView.postMessage(stringifyObj);
    bridgeUsed = true;
  }
  if (window?.webkit?.messageHandlers) {
    // post message to Ionic
    if (window?.webkit?.messageHandlers?.cordova_iab) {
      window.webkit.messageHandlers.cordova_iab.postMessage(stringifyObj);
    } else {
      window.parent.postMessage(stringifyObj, "*");
      window.postMessage(stringifyObj, "*");
    }

    bridgeUsed = true;

  }
  if (!bridgeUsed) {
    //  send this as a backup
    // console.log("postMsg (fallback):", stringifyObj);
    window.parent.postMessage(stringifyObj, "*"); // For iframes
    window.postMessage(stringifyObj, "*"); // For same-window webviews
  }
};

export const isMobileScreen = () => window.outerWidth < 992;

export const getBrowserLanguage = (): LanguageCode => {
  const langCode = window.localStorage.getItem("langCode") as LanguageCode;
  return SUPPORTED_LANGUAGES.includes(langCode) ? langCode : "en";
};

export const getDisabledDateRange = (refDate: Moment, dateLimit = moment().startOf("day")) =>
  refDate && refDate <= dateLimit;
export const getStartOfDay = (refDate?: Moment) => (refDate ? refDate.startOf("day") : moment().startOf("day"));

export const groupBy = (list: AvailableSeat[], keyGetter: any) => {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
};

export const previousValidTwoDays = (date: Moment) => {
  const dates = [date.clone().subtract(2, "days"), date.clone().subtract(1, "days")];

  return dates.filter((date) => date.isAfter(moment(), "day"));
};

export const generateNextDates = (date: Moment, limit: number) => {
  const dates = [];
  for (let i = 1; i <= limit; i++) {
    dates.push(date.clone().add(i, "days"));
  }

  return dates;
};

export const validateRequired = (_, value) => {
  if (!value || (value && value.trim() === "")) {
    return Promise.reject(new Error("Please enter a valid input"));
  }
  return Promise.resolve();
};

export const validateEmailRequired = (_, value) => {
  const trimmedValue = value.trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!trimmedValue || (trimmedValue && !validEmail.test(trimmedValue))) {
    return Promise.reject(new Error("Please enter a valid email address"));
  }

  return Promise.resolve();
};

export const validateNameRequired = (_, value, callback) => {
  if (!value || (value && value.trim() === "")) {
    return Promise.reject(new Error("Please enter a valid input"));
  }

  const trimmedValue = value.trim();

  const validNameRegex = /^[a-zA-Z]+(?:[ '\-.][a-zA-Z]+)*$/;
  const spaceRegex = /\s/;
  const numberRegex = /\d/;

  if (!validNameRegex.test(trimmedValue)) {
    callback("Special characters are not allowed");
  }

  if (spaceRegex.test(trimmedValue)) {
    callback("Name should not contain space");
  }

  if (numberRegex.test(trimmedValue)) {
    callback("Name should not contain numbers");
  }

  callback();
};

export const validatePhoneRequired = (_, value) => {
  const data = value.split(",");
  const phone = data[0];

  try {
    if (isSafaricomNumber(phone)) {
      return Promise.resolve();
    }
    return parsePhoneNumber(phone).valid
      ? Promise.resolve()
      : Promise.reject(new Error("Please enter a valid phone number"));
  } catch (err) {
    return Promise.reject(new Error("Please enter a valid phone number"));
  }
};

export const isSafaricomNumber = (phoneNumber: string): boolean => {
  const cleanNum = phoneNumber.replace(/[\s\+]/g, "");
  let formatted = cleanNum;
  if (formatted.startsWith("0")) {
    formatted = "254" + formatted.slice(1);
  } else if (!formatted.startsWith("254") && (formatted.startsWith("7") || formatted.startsWith("1"))) {
    formatted = "254" + formatted;
  }

  if (formatted.length !== 12 || !formatted.startsWith("254")) {
    return false;
  }

  const suffix = formatted.slice(3); // 9 digits
  const safaricomRegex = /^(?:7(?:0[0-9]|1[0-9]|2[0-9]|4[0-3568]|5[7-9]|6[89]|9[0-9])|11[0-9])[0-9]{6}$/;
  return safaricomRegex.test(suffix);
};



export const validatePassportNumberRequired = (_, value, callback) => {
  if (!value || (value && value.trim() === "")) {
    return Promise.reject(new Error("Please enter a valid passport number"));
  }

  const trimmedValue = value.trim();

  const specialCharactersRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
  const spaceRegex = /\s/;

  if (specialCharactersRegex.test(trimmedValue)) {
    return callback("Passport number should not contain special characters");
  }

  if (spaceRegex.test(trimmedValue)) {
    return callback("Passport number should not contain spaces");
  }

  if (trimmedValue.length < 6) {
    return callback("Passport number must be at least 6 characters");
  }

  return callback();
};


export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const toDate = (value: any) => {
  if (!value) return undefined;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.toDate() : undefined;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }

  const date = moment(value);
  return date.isValid() ? date.toDate() : undefined;
};


export const toSafeDate = (value: any): Date | undefined => {
  if (!value) return undefined;

  if (value?.DepartureDate) {
    return toSafeDate(value.DepartureDate);
  }

  if (value?._i?.from) {
    return toSafeDate(value._i.from);
  }

  if (value?.from && typeof value.from !== "function") {
    return toSafeDate(value.from);
  }

  if (value?.value) {
    return toSafeDate(value.value);
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  return undefined;
};

export const getMultiDestinationDate = (value: any): Date | undefined => {
  if (!value) return undefined;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.toDate() : undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (value?.from && typeof value.from !== "function") {
    return getMultiDestinationDate(value.from);
  }

  if (value?.value) {
    return getMultiDestinationDate(value.value);
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toDate() : undefined;
  }

  return undefined;
};

export const getMultiDestinationRange = (value: any) => {
  const from = getMultiDestinationDate(value);

  return from
    ? {
      from,
      to: undefined,
    }
    : undefined;
};

export const extractApiErrorMessage = (data: any, defaultMsg: string = "An error occurred."): string => {
  if (!data) return defaultMsg;
  if (typeof data === "string") return data;

  const errors = data.ErrorMessage || data.errorMessage;
  if (Array.isArray(errors) && errors.length > 0) {
    const filtered = errors.filter(Boolean);
    if (filtered.length > 0) return filtered.join(", ");
  }

  return data.Message || data.message || (typeof data.Data === "string" ? data.Data : "") || defaultMsg;
};
